// SCR-STAFF-933 — تقویم مسدودسازی منابع (US-ORG-020، US-ORG-021): مسدودسازی‌های ۳۱ روز آینده شعبه، ایجاد برای اتاق، واحد یا دستگاه با بازه
// یا تکرار روزانه/هفتگی/ماهانه با تاریخ پایان؛ رخدادها تا ۱۲ ماه آینده و تمدید غلتان (BR-ORG-022؛ C-AGENT-1262)، اتاق ← مسدودی واحدها و دستگاه‌های
// مستقر (BR-ORG-021)، ویرایش یک رخداد یا کل سری (AC-ORG-021.1) و لغو رخداد یا کل سری (AC-ORG-020.2).
import AddIcon from '@mui/icons-material/Add';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import {
  unwrap,
  type ResourceBlockReason,
  type ResourceBlockView,
  type ResourceKind,
} from '@phoenix/api-client';
import { useIdempotencyKey, useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  ConfirmDialog,
  DataTable,
  EmptyState,
  faDigits,
  FormDialog,
  JalaliDate,
  ListPage,
  JalaliDatePickerRHF,
  StatusChip,
  useDisplaySettings,
  type DataTableColumn,
} from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { WriteNotice } from '../settings/common';
import { BranchFilter, orgKeys, useBranchSelection } from './common';
import { BLOCK_REASONS, deviceKeys, todayIn, zonedToUtcIso } from './deviceCommon';

export const SCR_STAFF_933 = 'SCR-STAFF-933';

const WEEKDAYS = [6, 0, 1, 2, 3, 4, 5] as const;

export function ResourceBlocksPage() {
  const { t } = useTranslation('org');
  const { tenantApi, session } = useTenantSession();
  const write = useWriteAccess('org.resource_block.manage');
  const branches = useBranchSelection();
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [cancelling, setCancelling] = useState<ResourceBlockView | null>(null);
  const [editing, setEditing] = useState<ResourceBlockView | null>(null);
  const [wholeSeries, setWholeSeries] = useState(false);

  const blocks = useQuery({
    queryKey: [...deviceKeys.blocks(session?.tenantId), branches.branchId],
    queryFn: () =>
      unwrap(tenantApi.GET('/api/v1/organization/resource-blocks', { params: { query: { branchId: branches.branchId } } })),
    enabled: Boolean(branches.branchId),
  });

  const cancel = useMutation({
    mutationFn: (block: ResourceBlockView) =>
      unwrap(
        tenantApi.POST('/api/v1/organization/resource-blocks/{id}:cancel', {
          params: { path: { id: block.id } },
          body: { wholeSeries },
        }),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: deviceKeys.blocks(session?.tenantId) });
      setCancelling(null);
    },
  });

  const columns: DataTableColumn<ResourceBlockView>[] = [
    {
      id: 'resource',
      headerKey: 'org:blocks.columns.resource',
      cell: (row) => (
        <Stack direction="row" spacing={0.5} alignItems="center">
          <span>{row.resourceName ?? '—'}</span>
          <Chip size="small" variant="outlined" label={t(`org:resource_kind.${row.resourceKind}`)} />
        </Stack>
      ),
    },
    { id: 'from', headerKey: 'org:blocks.columns.from', cell: (row) => <JalaliDate value={row.from} withTime /> },
    { id: 'to', headerKey: 'org:blocks.columns.to', cell: (row) => <JalaliDate value={row.to} withTime /> },
    { id: 'reason', headerKey: 'org:blocks.columns.reason', cell: (row) => t(`org:block_reason.${row.reason}`) },
    {
      id: 'scope',
      headerKey: 'org:blocks.columns.scope',
      cell: (row) => (
        <Stack direction="row" spacing={0.5}>
          {Number(row.derivedCount) > 0 && <Chip size="small" label={t('org:blocks.derived', { n: faDigits(Number(row.derivedCount)) })} />}
          {row.seriesId && <Chip size="small" color="secondary" label={t('org:blocks.series')} />}
        </Stack>
      ),
    },
    {
      id: 'status',
      headerKey: 'org:blocks.columns.status',
      cell: (row) => (
        <span data-testid={`block-status-${row.id}`}>
          <StatusChip tone={row.status === 'Active' ? 'pending' : row.status === 'Scheduled' ? 'booked' : 'inactive'} labelKey={`org:block_status.${row.status}`} />
        </span>
      ),
    },
    {
      id: 'actions',
      headerKey: 'org:blocks.columns.actions',
      cell: (row) =>
        row.status === 'Scheduled' || row.status === 'Active' ? (
          <Stack direction="row" spacing={0.5}>
            {row.status === 'Scheduled' && !row.parentBlockId && !row.workOrderId && (
              <Button size="small" onClick={() => setEditing(row)} disabled={!write.canWrite} data-testid={`block-edit-${row.id}`}>
                {t('org:blocks.edit')}
              </Button>
            )}
            <Button
              size="small"
              color="error"
              onClick={() => {
                setWholeSeries(false);
                setCancelling(row);
              }}
              disabled={!write.canWrite}
              data-testid={`block-cancel-${row.id}`}
            >
              {row.status === 'Active' ? t('org:blocks.end_now') : t('org:blocks.cancel')}
            </Button>
            {row.seriesId && (
              <Button
                size="small"
                color="error"
                onClick={() => {
                  setWholeSeries(true);
                  setCancelling(row);
                }}
                disabled={!write.canWrite}
                data-testid={`block-cancel-series-${row.id}`}
              >
                {t('org:blocks.whole_series')}
              </Button>
            )}
          </Stack>
        ) : null,
    },
  ];

  return (
    <ListPage
      screenId={SCR_STAFF_933}
      titleKey="org:blocks.title"
      subtitleKey="org:blocks.subtitle"
      actions={
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreating(true)} disabled={!write.canWrite || !branches.branchId} data-testid="block-create">
          {t('org:blocks.add')}
        </Button>
      }
      filters={
        <Stack spacing={1}>
          <BranchFilter options={branches.options} value={branches.branchId} onChange={branches.setBranchId} />
          <WriteNotice write={write} />
        </Stack>
      }
    >
      {branches.options.length === 0 ? (
        <EmptyState titleKey="org:no_branch" />
      ) : (
        <DataTable
          captionKey="org:blocks.caption"
          columns={columns}
          rows={blocks.data?.items}
          getRowId={(row) => row.id}
          isLoading={blocks.isPending}
          error={blocks.error}
          onRetry={() => void blocks.refetch()}
          emptyTitleKey="org:blocks.empty"
        />
      )}
      {creating && branches.branchId && <BlockDialog branchId={branches.branchId} onClose={() => setCreating(false)} />}
      {editing && <EditBlockDialog block={editing} onClose={() => setEditing(null)} />}
      <ConfirmDialog
        open={cancelling !== null}
        titleKey="org:blocks.cancel_title"
        messageKey={wholeSeries ? 'org:blocks.cancel_series_message' : 'org:blocks.cancel_message'}
        messageValues={{ name: cancelling?.resourceName ?? '' }}
        confirmKey="org:blocks.cancel"
        destructive
        busy={cancel.isPending}
        onConfirm={() => cancelling && cancel.mutate(cancelling)}
        onCancel={() => setCancelling(null)}
        error={cancel.error ?? undefined}
      />
    </ListPage>
  );
}

interface ResourceOption {
  id: string;
  label: string;
}

function BlockDialog({ branchId, onClose }: { branchId: string; onClose: () => void }) {
  const { t } = useTranslation('org');
  const { tenantApi, session } = useTenantSession();
  const { timeZone } = useDisplaySettings();
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const [kind, setKind] = useState<ResourceKind>('Room');
  const [resourceId, setResourceId] = useState('');
  const [reason, setReason] = useState<ResourceBlockReason>('Maintenance');
  const [note, setNote] = useState('');
  const [mode, setMode] = useState<'single' | 'daily' | 'weekly' | 'monthly'>('single');
  const dates = useForm<{ startDate: string | null; endDate: string | null }>({
    defaultValues: { startDate: todayIn(timeZone, 1), endDate: todayIn(timeZone, 1) },
  });
  const startDate = dates.watch('startDate') ?? todayIn(timeZone, 1);
  const endDate = dates.watch('endDate') ?? startDate;
  const [startTime, setStartTime] = useState('13:00');
  const [endTime, setEndTime] = useState('13:30');
  const [weekdays, setWeekdays] = useState<number[]>([6]);

  const rooms = useQuery({
    queryKey: [...orgKeys.rooms(session?.tenantId), branchId, 'block-options'],
    queryFn: () => unwrap(tenantApi.GET('/api/v1/organization/rooms', { params: { query: { branchId, status: 'Active' } } })),
  });
  const units = useQuery({
    queryKey: [...deviceKeys.units(session?.tenantId), branchId, 'block-options'],
    queryFn: () => unwrap(tenantApi.GET('/api/v1/organization/bookable-units', { params: { query: { branchId } } })),
    enabled: kind === 'BookableUnit',
  });
  const devices = useQuery({
    queryKey: [...deviceKeys.devices(session?.tenantId), branchId, 'block-options'],
    queryFn: () => unwrap(tenantApi.GET('/api/v1/organization/devices', { params: { query: { branchId, limit: 200 } } })),
    enabled: kind === 'Device',
  });
  const options: ResourceOption[] =
    kind === 'Room'
      ? (rooms.data?.items ?? []).map((room) => ({ id: room.id, label: room.name }))
      : kind === 'BookableUnit'
        ? (units.data?.items ?? [])
            .filter((unit) => unit.status === 'Active')
            .map((unit) => ({ id: unit.id, label: `${rooms.data?.items.find((room) => room.id === unit.roomId)?.name ?? ''} — ${unit.name}` }))
        : (devices.data?.items ?? []).filter((device) => device.status !== 'Retired').map((device) => ({ id: device.id, label: device.name }));

  const save = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/organization/resource-blocks', {
          body:
            mode === 'single'
              ? {
                  resourceKind: kind,
                  resourceId,
                  reason,
                  note: note.trim() || null,
                  from: zonedToUtcIso(startDate, startTime, timeZone),
                  to: zonedToUtcIso(endDate, endTime, timeZone),
                  recurrence: null,
                }
              : {
                  resourceKind: kind,
                  resourceId,
                  reason,
                  note: note.trim() || null,
                  from: null,
                  to: null,
                  recurrence: {
                    pattern: mode === 'daily' ? 'Daily' : mode === 'weekly' ? 'Weekly' : 'Monthly',
                    weekdays: mode === 'weekly' ? weekdays : [],
                    startDate,
                    endDate: endDate > startDate ? endDate : null,
                    startTime,
                    endTime,
                    occurrences: null,
                  },
                },
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      ),
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: deviceKeys.blocks(session?.tenantId) });
      onClose();
    },
  });

  return (
    <FormDialog
      open
      screenId="SCR-STAFF-933-FORM"
      titleKey="org:blocks.create_title"
      onSubmit={() => resourceId && save.mutate()}
      onClose={onClose}
      isDirty={Boolean(resourceId)}
      isSubmitting={save.isPending}
      submitError={save.error ?? undefined}
    >
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField
          select
          fullWidth
          label={t('org:blocks.form.kind')}
          value={kind}
          onChange={(event) => {
            setKind(event.target.value as ResourceKind);
            setResourceId('');
          }}
          slotProps={{ htmlInput: { 'data-testid': 'block-kind' } }}
        >
          {(['Room', 'BookableUnit', 'Device'] as const).map((value) => (
            <MenuItem key={value} value={value}>
              {t(`org:resource_kind.${value}`)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          fullWidth
          required
          label={t('org:blocks.form.resource')}
          value={resourceId}
          onChange={(event) => setResourceId(event.target.value)}
          slotProps={{ htmlInput: { 'data-testid': 'block-resource' } }}
        >
          {options.map((option) => (
            <MenuItem key={option.id} value={option.id}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      </Stack>
      {kind === 'Room' && <Alert severity="info">{t('org:blocks.form.room_hint')}</Alert>}
      <TextField
        select
        fullWidth
        label={t('org:blocks.form.reason')}
        value={reason}
        onChange={(event) => setReason(event.target.value as ResourceBlockReason)}
        slotProps={{ htmlInput: { 'data-testid': 'block-reason' } }}
      >
        {BLOCK_REASONS.map((value) => (
          <MenuItem key={value} value={value}>
            {t(`org:block_reason.${value}`)}
          </MenuItem>
        ))}
      </TextField>
      <ToggleButtonGroup exclusive size="small" value={mode} onChange={(_event, value: typeof mode | null) => value && setMode(value)} aria-label={t('org:blocks.form.repeat')}>
        <ToggleButton value="single" data-testid="block-mode-single">
          {t('org:blocks.form.single')}
        </ToggleButton>
        <ToggleButton value="daily" data-testid="block-mode-daily">
          {t('org:blocks.form.daily')}
        </ToggleButton>
        <ToggleButton value="weekly" data-testid="block-mode-weekly">
          {t('org:blocks.form.weekly')}
        </ToggleButton>
        <ToggleButton value="monthly" data-testid="block-mode-monthly">
          {t('org:blocks.form.monthly')}
        </ToggleButton>
      </ToggleButtonGroup>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <JalaliDatePickerRHF control={dates.control} name="startDate" labelKey="org:blocks.form.start_date" required />
        <TextField type="time" fullWidth label={t('org:blocks.form.start_time')} value={startTime} onChange={(event) => setStartTime(event.target.value)} slotProps={{ inputLabel: { shrink: true }, htmlInput: { 'data-testid': 'block-start-time' } }} />
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <JalaliDatePickerRHF
          control={dates.control}
          name="endDate"
          labelKey={mode === 'single' ? 'org:blocks.form.end_date' : 'org:blocks.form.until'}
          helperKey={mode === 'single' ? undefined : 'org:blocks.form.until_help'}
        />
        <TextField type="time" fullWidth label={t('org:blocks.form.end_time')} value={endTime} onChange={(event) => setEndTime(event.target.value)} slotProps={{ inputLabel: { shrink: true }, htmlInput: { 'data-testid': 'block-end-time' } }} />
      </Stack>
      {mode === 'weekly' && (
        <ToggleButtonGroup
          size="small"
          value={weekdays}
          onChange={(_event, value: number[]) => setWeekdays(value)}
          aria-label={t('org:blocks.form.weekdays')}
        >
          {WEEKDAYS.map((day) => (
            <ToggleButton key={day} value={day} data-testid={`block-weekday-${day}`}>
              {t(`org:weekday.${day}`)}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      )}
      <TextField fullWidth multiline minRows={2} label={t('org:blocks.form.note')} value={note} onChange={(event) => setNote(event.target.value)} slotProps={{ htmlInput: { maxLength: 500, 'data-testid': 'block-note' } }} />
    </FormDialog>
  );
}


/**
 * ویرایش مسدودسازی آینده (AC-ORG-021.1): «فقط این رخداد» ← بازه تازه (لغو و جایگزینی در همان سری)؛ «کل سری» ← ساعت و توضیح همه رخدادهای آینده.
 */
function EditBlockDialog({ block, onClose }: { block: ResourceBlockView; onClose: () => void }) {
  const { t } = useTranslation('org');
  const { tenantApi, session } = useTenantSession();
  const { timeZone } = useDisplaySettings();
  const queryClient = useQueryClient();
  const [scope, setScope] = useState<'Occurrence' | 'Series'>('Occurrence');
  const local = (value: string, part: 'date' | 'time') => {
    const parts = new Intl.DateTimeFormat('en-CA', { timeZone, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }).formatToParts(new Date(value));
    const get = (type: string) => parts.find((item) => item.type === type)?.value ?? '00';
    return part === 'date' ? `${get('year')}-${get('month')}-${get('day')}` : `${get('hour')}:${get('minute')}`;
  };
  const dates = useForm<{ date: string | null }>({ defaultValues: { date: local(block.from, 'date') } });
  const date = dates.watch('date') ?? local(block.from, 'date');
  const [startTime, setStartTime] = useState(local(block.from, 'time'));
  const [endTime, setEndTime] = useState(local(block.to, 'time'));
  const [note, setNote] = useState(block.note ?? '');
  const save = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.PUT('/api/v1/organization/resource-blocks/{id}', {
          params: { path: { id: block.id } },
          body:
            scope === 'Occurrence'
              ? { scope, from: zonedToUtcIso(date, startTime, timeZone), to: zonedToUtcIso(date, endTime, timeZone), startTime: null, endTime: null, note: note.trim() || null }
              : { scope, from: null, to: null, startTime, endTime, note: note.trim() || null },
        }),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: deviceKeys.blocks(session?.tenantId) });
      onClose();
    },
  });
  return (
    <FormDialog open screenId="SCR-STAFF-933-EDIT" titleKey="org:blocks.edit_title" onSubmit={() => save.mutate()} onClose={onClose} isDirty isSubmitting={save.isPending} submitError={save.error ?? undefined}>
      {block.seriesId && (
        <ToggleButtonGroup exclusive size="small" value={scope} onChange={(_event, value: typeof scope | null) => value && setScope(value)} aria-label={t('org:blocks.form.scope')}>
          <ToggleButton value="Occurrence" data-testid="block-edit-occurrence">
            {t('org:blocks.form.this_occurrence')}
          </ToggleButton>
          <ToggleButton value="Series" data-testid="block-edit-series">
            {t('org:blocks.form.whole_series')}
          </ToggleButton>
        </ToggleButtonGroup>
      )}
      {scope === 'Occurrence' ? <JalaliDatePickerRHF control={dates.control} name="date" labelKey="org:blocks.form.start_date" required /> : <Alert severity="info">{t('org:blocks.form.series_hint')}</Alert>}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField type="time" fullWidth label={t('org:blocks.form.start_time')} value={startTime} onChange={(event) => setStartTime(event.target.value)} slotProps={{ inputLabel: { shrink: true }, htmlInput: { 'data-testid': 'block-edit-start-time' } }} />
        <TextField type="time" fullWidth label={t('org:blocks.form.end_time')} value={endTime} onChange={(event) => setEndTime(event.target.value)} slotProps={{ inputLabel: { shrink: true }, htmlInput: { 'data-testid': 'block-edit-end-time' } }} />
      </Stack>
      <TextField fullWidth multiline minRows={2} label={t('org:blocks.form.note')} value={note} onChange={(event) => setNote(event.target.value)} slotProps={{ htmlInput: { maxLength: 500, 'data-testid': 'block-edit-note' } }} />
    </FormDialog>
  );
}
